import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { useAuth } from '../../context/authContext';
import useTicket from '../../hooks/useTicket';
import { isFeatureEnabled } from '../../config/featureFlags';
import BookingDetailsModal from '../Bookings/BookingDetailsModal/BookingDetailsModal';

// QR payloads on booking tickets start with one of these prefixes (see backend
// ticket-handler): TKT is the current layout-safe prefix, FZT: the legacy one,
// and FYTÖ is how a US-keycode wedge scanner types FZT: on a German (QWERTZ)
// keyboard layout. The raw buffer is sent to the backend, which normalizes all
// variants including the Y/Z layout swap.
const TICKET_PREFIXES = ['TKT', 'FZT:', 'FYTÖ'];
// Handheld wedge scanners type characters with only a few ms between keystrokes;
// anything slower is treated as human typing and ignored
const MAX_KEY_GAP_MS = 150;
// Persisted per device so a fixed reception PC keeps its check-in setting
const AUTO_CHECKIN_STORAGE_KEY = 'ticketScanAutoCheckIn';

const STATUS_LABELS = {
  confirmed: 'Bestätigt',
  canceled: 'Storniert',
  noShow: 'No-Show',
  awaitingConfirmation: 'Wartet auf Bestätigung',
  rejected: 'Abgelehnt',
  waitingForPayment: 'Wartet auf Zahlung',
  blocked: 'Blockiert',
};

// Global keyboard-wedge listener: detects a scanned ticket QR payload
// (FZT:<code> + Enter) from any authenticated view, validates the ticket,
// checks the customer in (per the persisted auto-check-in setting) and opens
// the booking details modal in place — without leaving the current view.
export default function TicketScanWedgeListener() {
  const auth = useAuth();
  const { scanTicket } = useTicket();

  const [modalBookingId, setModalBookingId] = useState(null);

  const bufferRef = useRef('');
  const lastKeyAtRef = useRef(0);
  const capturingRef = useRef(false);
  const processingRef = useRef(false);

  const enabled =
    isFeatureEnabled('qrTickets') &&
    Boolean(auth?.token) &&
    Boolean(auth?.checkRightAny?.('ticket:scan'));

  const handleScannedCode = async code => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      const autoCheckIn = localStorage.getItem(AUTO_CHECKIN_STORAGE_KEY) !== 'false';
      const response = await scanTicket({ code, checkIn: autoCheckIn });
      if (!response?.booking) return;

      const { result, checkedIn, booking } = response;
      const name = `${booking.firstName || ''} ${booking.lastName || ''}`.trim();

      if (checkedIn) {
        toast.success(`${name} wurde eingecheckt.`);
      } else if (result === 'alreadyCheckedIn') {
        toast.warning(
          `${name} ist bereits seit ${dayjs(booking.presentSince).format('HH:mm')} Uhr eingecheckt.`
        );
      } else if (result === 'wrongDay') {
        toast.warning(
          `Achtung: Buchung ${booking.bookingNumber} ist für den ${dayjs(booking.startTime).format(
            'DD.MM.YYYY'
          )} – nicht für heute.`
        );
      } else if (result === 'invalidStatus') {
        toast.error(
          `Buchung ${booking.bookingNumber} ist nicht gültig (Status: ${
            STATUS_LABELS[booking.status] || booking.status
          }).`
        );
      } else if (result === 'ok' && !checkedIn) {
        toast.info(`Ticket gültig: ${name} (${booking.bookingNumber}).`);
      }

      setModalBookingId(booking._id);
    } catch (err) {
      toast.error('Ticket nicht gefunden oder ungültig. Bitte Code prüfen.');
    } finally {
      processingRef.current = false;
    }
  };

  useEffect(() => {
    if (!enabled) return undefined;

    const reset = () => {
      bufferRef.current = '';
      capturingRef.current = false;
    };

    const handleKeyDown = event => {
      const now = Date.now();
      if (now - lastKeyAtRef.current > MAX_KEY_GAP_MS) reset();
      lastKeyAtRef.current = now;

      if (event.key === 'Enter') {
        const upper = bufferRef.current.toUpperCase();
        const matchedPrefix = TICKET_PREFIXES.find(prefix => upper.startsWith(prefix));
        if (capturingRef.current && matchedPrefix && upper.length > matchedPrefix.length) {
          event.preventDefault();
          event.stopPropagation();
          const code = bufferRef.current;
          reset();
          void handleScannedCode(code);
        } else {
          reset();
        }
        return;
      }

      if (event.key.length !== 1) return;

      bufferRef.current += event.key;

      if (!capturingRef.current) {
        const upper = bufferRef.current.toUpperCase();
        if (TICKET_PREFIXES.some(prefix => upper.startsWith(prefix))) {
          capturingRef.current = true;
        } else if (!TICKET_PREFIXES.some(prefix => prefix.startsWith(upper))) {
          // Fast input that does not build towards any ticket prefix
          bufferRef.current = '';
        }
      }

      // Once identified as a ticket scan, keep the payload out of focused inputs
      if (capturingRef.current) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  if (!modalBookingId) return null;

  return (
    <BookingDetailsModal
      open
      onClose={() => setModalBookingId(null)}
      bookingId={modalBookingId}
    />
  );
}
